import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { sendBytes, b64ToBytes, webusbSupported } from "@/utils/webusbPrinter";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Printer,
  DeviceTablet,
  DeviceMobile,
  Money,
  Barcode,
  Monitor,
  Desktop,
  Screencast,
  Plus,
  PencilSimple,
  Trash,
  Circle,
  MagnifyingGlass,
  TestTube,
  CashRegister,
  Usb,
  WifiHigh,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const EQUIPMENT_TYPES = [
  { value: "printer", label: "Printer", icon: Printer },
  { value: "tablet", label: "Tablet", icon: DeviceTablet },
  { value: "handheld", label: "Handheld", icon: DeviceMobile },
  { value: "cash_drawer", label: "Cash Drawer", icon: Money },
  { value: "scanner", label: "Scanner", icon: Barcode },
  { value: "kds", label: "KDS", icon: Monitor },
  { value: "workstation", label: "Workstation", icon: Desktop },
  { value: "customer_display", label: "Customer Display", icon: Screencast },
];

const STATUS_OPTIONS = [
  { value: "online", label: "Online", color: "bg-green-500" },
  { value: "offline", label: "Offline", color: "bg-gray-400" },
  { value: "maintenance", label: "Maintenance", color: "bg-orange-500" },
];

const emptyForm = {
  name: "",
  type: "printer",
  model_name: "",
  serial_number: "",
  connection_type: "network",   // "network" | "usb"
  ip_address: "",
  device_path: "",              // e.g. "/dev/usb/lp0"
  mac_address: "",
  location: "",
  assigned_station: "",
  status: "offline",
  notes: "",
};

const EquipmentPage = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Set of "vendor:product" strings the browser has active USB permission
  // for. Fed by navigator.usb.getDevices() so we can flash "(Paired)" next
  // to each WebUSB printer that this specific terminal has already set up.
  const [pairedUsbIds, setPairedUsbIds] = useState(new Set());

  useEffect(() => { fetchEquipment(); }, []);

  // Discover which WebUSB devices this browser has already been granted
  // permission for. Runs once on mount and after every printer save (via
  // fetchEquipment) so pairing changes reflect immediately.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      if (!webusbSupported()) return;
      try {
        const list = await navigator.usb.getDevices();
        if (cancelled) return;
        setPairedUsbIds(new Set(list.map((d) => `${d.vendorId}:${d.productId}`)));
      } catch { /* ignore — some contexts disallow getDevices() */ }
    };
    refresh();
    return () => { cancelled = true; };
  }, [equipment]);

  // Auto-poll printer status every 60 s so the Online/Offline badges stay
  // in sync with the backend probe loop. The probe itself sends zero bytes,
  // so it cannot trigger a Wi-Fi config print on any printer.
  useEffect(() => {
    const t = setInterval(() => { fetchEquipment(); }, 60000);
    return () => clearInterval(t);
  }, []);

  const handleProbeAll = async () => {
    try {
      const res = await axios.post(`${API}/printers/probe`);
      toast.success(`Probed ${res.data?.probed || 0} printer${res.data?.probed === 1 ? "" : "s"} — ${res.data?.online || 0} online`);
      await fetchEquipment();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed");
    }
  };

  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/equipment`);
      setEquipment(res.data);
    } catch (err) {
      toast.error("Failed to load equipment");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/equipment/${editing.id}`, form);
        toast.success("Equipment updated");
      } else {
        await axios.post(`${API}/equipment`, form);
        toast.success("Equipment added");
      }
      setShowDialog(false);
      setEditing(null);
      setForm(emptyForm);
      fetchEquipment();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      type: item.type || "printer",
      model_name: item.model_name || "",
      serial_number: item.serial_number || "",
      connection_type: item.connection_type || (item.device_path ? "usb" : "network"),
      ip_address: item.ip_address || "",
      device_path: item.device_path || "",
      mac_address: item.mac_address || "",
      location: item.location || "",
      assigned_station: item.assigned_station || "",
      status: item.status || "offline",
      notes: item.notes || "",
    });
    setShowDialog(true);
  };

  const handleTestPrint = async (item) => {
    try {
      const res = await axios.post(`${API}/printers/${item.id}/test-print`);
      // WebUSB: backend returned the payload for the browser to transferOut.
      if (res.data?.browser_handoff && res.data?.payload_b64) {
        try {
          await sendBytes(
            {
              vendorId: res.data.webusb_vendor_id,
              productId: res.data.webusb_product_id,
              serialNumber: res.data.webusb_serial_number,
            },
            b64ToBytes(res.data.payload_b64),
          );
          toast.success(`Test receipt sent to ${item.name} via WebUSB`);
        } catch (e) {
          toast.error(e?.message || "WebUSB print failed");
        }
        return;
      }
      if (res.data?.ok) {
        toast.success(`Test receipt sent to ${item.name} via ${res.data.transport}`);
      } else {
        toast.error(`Test print failed: ${res.data?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test print failed");
    }
  };

  const handleTestDrawer = async (item) => {
    try {
      const res = await axios.post(`${API}/cash-drawer/test/${item.id}`);
      if (res.data?.browser_handoff && res.data?.payload_b64) {
        try {
          await sendBytes(
            {
              vendorId: res.data.webusb_vendor_id,
              productId: res.data.webusb_product_id,
              serialNumber: res.data.webusb_serial_number,
            },
            b64ToBytes(res.data.payload_b64),
          );
          toast.success(`Drawer kick sent to ${item.name} via WebUSB`);
        } catch (e) {
          toast.error(e?.message || "WebUSB drawer kick failed");
        }
        return;
      }
      if (res.data?.ok) {
        toast.success(`Drawer kick fired through ${item.name} (${res.data.transport})`);
      } else {
        toast.error(`Drawer kick failed: ${res.data?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drawer kick failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/equipment/${id}`);
      toast.success("Equipment deleted");
      fetchEquipment();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const getTypeIcon = (type) => {
    const t = EQUIPMENT_TYPES.find((e) => e.value === type);
    return t ? t.icon : Desktop;
  };

  const getTypeLabel = (type) => {
    const t = EQUIPMENT_TYPES.find((e) => e.value === type);
    return t ? t.label : type;
  };

  const getStatusInfo = (status) => {
    const s = STATUS_OPTIONS.find((o) => o.value === status);
    return s || STATUS_OPTIONS[1];
  };

  const filtered = equipment.filter((item) => {
    const matchTab = activeTab === "all" || item.type === activeTab;
    const matchSearch =
      !search ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      (item.ip_address || "").includes(search) ||
      (item.location || "").toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const counts = EQUIPMENT_TYPES.map((t) => ({
    ...t,
    count: equipment.filter((e) => e.type === t.value).length,
  }));

  const onlineCount = equipment.filter((e) => e.status === "online").length;

  return (
    <div className="min-h-screen bg-background" data-testid="equipment-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Equipment</h1>
            <p className="text-muted-foreground">
              Manage POS hardware devices and peripherals
            </p>
          </div>
          <Button className="gap-2" onClick={handleAdd} data-testid="add-equipment-btn">
            <Plus size={18} /> Add Device
          </Button>
        </div>

        {/* Probe printers button — uses the byte-free reachability probe */}
        {equipment.some((e) => e.type === "printer") && (
          <div className="flex justify-end mb-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleProbeAll}
              data-testid="probe-printers-btn"
              title="Reachability check — sends zero bytes to printers"
            >
              <ArrowsClockwise size={14} weight="bold" />
              Refresh Printer Status
            </Button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Desktop size={20} className="text-primary" weight="fill" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Devices</p>
                <p className="font-mono text-xl font-medium" data-testid="equipment-total">{equipment.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Circle size={20} className="text-green-600" weight="fill" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Online</p>
                <p className="font-mono text-xl font-medium" data-testid="equipment-online">{onlineCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <Circle size={20} className="text-gray-400" weight="fill" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Offline</p>
                <p className="font-mono text-xl font-medium">{equipment.filter((e) => e.status === "offline").length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Circle size={20} className="text-orange-500" weight="fill" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Maintenance</p>
                <p className="font-mono text-xl font-medium">{equipment.filter((e) => e.status === "maintenance").length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-secondary/50 h-auto p-1 flex-wrap">
              <TabsTrigger value="all" className="text-xs px-3 py-1.5" data-testid="tab-all">
                All ({equipment.length})
              </TabsTrigger>
              {counts.filter((c) => c.count > 0).map((c) => (
                <TabsTrigger key={c.value} value={c.value} className="text-xs px-3 py-1.5" data-testid={`tab-${c.value}`}>
                  {c.label} ({c.count})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search devices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="equipment-search"
            />
          </div>
        </div>

        {/* Equipment List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <Desktop size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No devices found</p>
              <p className="text-sm">Add your first device to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((item) => {
              const Icon = getTypeIcon(item.type);
              const statusInfo = getStatusInfo(item.status);
              return (
                <Card key={item.id} className="hover:shadow-sm transition-shadow" data-testid={`equipment-${item.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                      <Icon size={22} className="text-primary" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium truncate">{item.name}</p>
                        <Badge variant="outline" className="text-xs shrink-0">{getTypeLabel(item.type)}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        {item.type === "printer" && (
                          item.connection_type === "webusb" ? (
                            <span className="font-mono inline-flex items-center gap-1" data-testid={`webusb-status-${item.id}`}>
                              <Usb size={12} weight="fill" />
                              WebUSB
                              {item.webusb_vendor_id && pairedUsbIds.has(`${item.webusb_vendor_id}:${item.webusb_product_id}`) ? (
                                <span className="text-green-600 dark:text-green-400 font-sans font-medium">(Paired)</span>
                              ) : (
                                <span className="text-amber-600 dark:text-amber-400 font-sans font-medium">
                                  {item.webusb_vendor_id ? "(Not paired here)" : "(Not paired)"}
                                </span>
                              )}
                            </span>
                          ) : item.connection_type === "usb" || (item.device_path && !item.ip_address) ? (
                            <span className="font-mono inline-flex items-center gap-1"><Usb size={12} weight="fill" /> {item.device_path || "USB"}</span>
                          ) : item.ip_address ? (
                            <span className="font-mono inline-flex items-center gap-1"><WifiHigh size={12} weight="fill" /> {item.ip_address}</span>
                          ) : null
                        )}
                        {item.type !== "printer" && item.ip_address && <span className="font-mono">{item.ip_address}</span>}
                        {item.model_name && <span>{item.model_name}</span>}
                        {item.location && <span>{item.location}</span>}
                        {item.assigned_station && <span>Station: {item.assigned_station}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${statusInfo.color}`} />
                        <span className="text-xs font-medium capitalize">{statusInfo.label}</span>
                      </div>
                      {item.type === "printer" && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Send test receipt"
                            onClick={() => handleTestPrint(item)}
                            data-testid={`test-print-${item.id}`}
                          >
                            <TestTube size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Fire drawer kick"
                            onClick={() => handleTestDrawer(item)}
                            data-testid={`test-drawer-${item.id}`}
                          >
                            <CashRegister size={16} />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} data-testid={`edit-equipment-${item.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-equipment-${item.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(o) => { if (!o) { setShowDialog(false); setEditing(null); } }}>
          <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto" data-testid="equipment-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editing ? "Edit Device" : "Add Device"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Device Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kitchen Printer 1" data-testid="equipment-name" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="equipment-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Input value={form.model_name} onChange={(e) => setForm({ ...form, model_name: e.target.value })} placeholder="e.g. Volcora VRT-80VI" data-testid="equipment-model" />
                </div>
                <div className="space-y-2">
                  <Label>Serial Number</Label>
                  <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="e.g. SN12345678" data-testid="equipment-serial" />
                </div>
              </div>

              {/* Connection: Network IP vs USB device path — only meaningful for printers */}
              {form.type === "printer" && (
                <div className="space-y-2 p-3 rounded-lg border bg-secondary/20" data-testid="printer-connection-block">
                  <Label>Connection</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={form.connection_type === "network" ? "default" : "outline"}
                      className="gap-1.5"
                      onClick={() => setForm({ ...form, connection_type: "network" })}
                      data-testid="connection-network-btn"
                    >
                      <WifiHigh size={16} weight="fill" /> Network (TCP)
                    </Button>
                    <Button
                      type="button"
                      variant={form.connection_type === "usb" ? "default" : "outline"}
                      className="gap-1.5"
                      onClick={() => setForm({ ...form, connection_type: "usb" })}
                      data-testid="connection-usb-btn"
                    >
                      <Usb size={16} weight="fill" /> USB
                    </Button>
                  </div>
                  {form.connection_type === "network" ? (
                    <div className="space-y-1.5 pt-1">
                      <Label className="text-xs">Printer IP Address</Label>
                      <Input
                        value={form.ip_address}
                        onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                        placeholder="e.g. 192.168.1.100"
                        data-testid="equipment-ip"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Volcora / Epson / Star printers listen on raw TCP <span className="font-mono">9100</span> by default.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 pt-1">
                      <Label className="text-xs">USB Device Path</Label>
                      <Input
                        value={form.device_path}
                        onChange={(e) => setForm({ ...form, device_path: e.target.value })}
                        placeholder="/dev/usb/lp0"
                        data-testid="equipment-device-path"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Plug in the Volcora, then run <span className="font-mono">ls /dev/usb/lp*</span> on the
                        POS host. Add the running user to the <span className="font-mono">lp</span> group
                        (<span className="font-mono">sudo usermod -aG lp $USER</span>) and log out / back in if you hit permission errors.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* For non-printer devices, keep the legacy IP field */}
              {form.type !== "printer" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IP Address</Label>
                    <Input value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} placeholder="e.g. 192.168.1.100" data-testid="equipment-ip" />
                  </div>
                  <div className="space-y-2">
                    <Label>MAC Address</Label>
                    <Input value={form.mac_address} onChange={(e) => setForm({ ...form, mac_address: e.target.value })} placeholder="e.g. AA:BB:CC:DD:EE:FF" data-testid="equipment-mac" />
                  </div>
                </div>
              )}

              {form.type === "printer" && (
                <div className="space-y-2">
                  <Label>MAC Address (optional)</Label>
                  <Input value={form.mac_address} onChange={(e) => setForm({ ...form, mac_address: e.target.value })} placeholder="e.g. AA:BB:CC:DD:EE:FF" data-testid="equipment-mac" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Kitchen, Bar, Host Stand" data-testid="equipment-location" />
                </div>
                <div className="space-y-2">
                  <Label>Assigned Station</Label>
                  <Input value={form.assigned_station} onChange={(e) => setForm({ ...form, assigned_station: e.target.value })} placeholder="e.g. POS 1, Bar Station" data-testid="equipment-station" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="equipment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." rows={2} data-testid="equipment-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-equipment-btn">{editing ? "Save Changes" : "Add Device"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EquipmentPage;
